/**
 * Audio Processor Service
 *
 * Handles real-time audio processing:
 * - Noise suppression (RNNoise)
 * - Integration with existing VAD
 *
 * This service processes audio frames and returns cleaned audio.
 * It's designed to integrate with the existing audioCapture.js flow.
 */

import { getCapabilities } from './deviceCapabilities.js';
import { audioConfig, getAggregatedRequirements } from '../config/audioProcessing.js';
import { RNNoiseProvider } from './audio/noiseSuppression/RNNoiseProvider.js';
import { NullProvider } from './audio/noiseSuppression/NullProvider.js';

/**
 * Audio Processor State
 */
export const ProcessorState = {
  UNINITIALIZED: 'uninitialized',
  INITIALIZING: 'initializing',
  READY: 'ready',
  ERROR: 'error',
};

class AudioProcessor {
  constructor() {
    this.state = ProcessorState.UNINITIALIZED;
    this.capabilities = null;
    this.noiseSuppressionProvider = null;
    this.noiseSuppressionEnabled = true;

    // Callbacks
    this.onStateChange = null;
    this.onError = null;
  }

  /**
   * Set state and notify listeners
   */
  setState(state) {
    this.state = state;
    console.log('[AudioProcessor] State changed to:', state);
    if (this.onStateChange) {
      this.onStateChange(state);
    }
  }

  /**
   * Initialize the audio processor
   * @param {Object} options
   * @param {boolean} options.enableNoiseSuppression - Enable/disable noise suppression
   */
  async initialize(options = {}) {
    if (this.state === ProcessorState.READY) {
      console.log('[AudioProcessor] Already initialized');
      return;
    }

    this.setState(ProcessorState.INITIALIZING);

    try {
      // Check device capabilities
      this.capabilities = await getCapabilities();
      console.log('[AudioProcessor] Device capabilities:', {
        wasmSimd: this.capabilities.wasmSimd,
        webgpu: this.capabilities.webgpu,
        memory: this.capabilities.memory,
      });

      // Initialize noise suppression
      this.noiseSuppressionEnabled = options.enableNoiseSuppression !== false &&
                                     audioConfig.noiseSuppression.enabled;

      if (this.noiseSuppressionEnabled) {
        await this.initializeNoiseSuppression();
      } else {
        console.log('[AudioProcessor] Noise suppression disabled');
        this.noiseSuppressionProvider = new NullProvider();
        await this.noiseSuppressionProvider.initialize();
      }

      this.setState(ProcessorState.READY);
      console.log('[AudioProcessor] Initialized successfully');
    } catch (error) {
      console.error('[AudioProcessor] Initialization failed:', error);
      this.setState(ProcessorState.ERROR);
      if (this.onError) {
        this.onError(error);
      }
      throw error;
    }
  }

  /**
   * Initialize noise suppression provider
   */
  async initializeNoiseSuppression() {
    const provider = audioConfig.noiseSuppression.provider;
    console.log('[AudioProcessor] Initializing noise suppression:', provider);

    if (provider === 'rnnoise') {
      if (!this.capabilities.wasmSimd) {
        console.warn('[AudioProcessor] WASM SIMD not supported, disabling RNNoise');
        this.noiseSuppressionProvider = new NullProvider();
      } else {
        this.noiseSuppressionProvider = new RNNoiseProvider();
      }
    } else {
      this.noiseSuppressionProvider = new NullProvider();
    }

    await this.noiseSuppressionProvider.initialize();
    console.log('[AudioProcessor] Noise suppression ready:', this.noiseSuppressionProvider.getName());
  }

  /**
   * Process audio samples through the pipeline
   *
   * @param {Float32Array} samples - Input audio samples (normalized -1 to 1)
   * @param {number} sampleRate - Input sample rate
   * @returns {Float32Array} - Processed audio samples
   */
  process(samples, sampleRate) {
    if (this.state !== ProcessorState.READY) {
      // Return original samples if not ready
      return samples;
    }

    let processedSamples = samples;

    // Apply noise suppression
    if (this.noiseSuppressionProvider?.isReady()) {
      processedSamples = this.noiseSuppressionProvider.process(processedSamples, sampleRate);
    }

    return processedSamples;
  }

  /**
   * Enable/disable noise suppression at runtime
   */
  setNoiseSuppressionEnabled(enabled) {
    this.noiseSuppressionEnabled = enabled;
    console.log('[AudioProcessor] Noise suppression:', enabled ? 'enabled' : 'disabled');
  }

  /**
   * Check if noise suppression is active
   */
  isNoiseSuppressionActive() {
    return this.noiseSuppressionEnabled &&
           this.noiseSuppressionProvider?.isReady() &&
           this.noiseSuppressionProvider?.getName() !== 'none';
  }

  /**
   * Get current state
   */
  getState() {
    return {
      state: this.state,
      noiseSuppressionEnabled: this.noiseSuppressionEnabled,
      noiseSuppressionProvider: this.noiseSuppressionProvider?.getName(),
      capabilities: this.capabilities,
    };
  }

  /**
   * Check if processor is ready
   */
  isReady() {
    return this.state === ProcessorState.READY;
  }

  /**
   * Clean up resources
   */
  async destroy() {
    console.log('[AudioProcessor] Destroying...');

    if (this.noiseSuppressionProvider) {
      await this.noiseSuppressionProvider.destroy();
      this.noiseSuppressionProvider = null;
    }

    this.capabilities = null;
    this.setState(ProcessorState.UNINITIALIZED);
    console.log('[AudioProcessor] Destroyed');
  }
}

// Singleton instance
let audioProcessorInstance = null;

/**
 * Get the singleton Audio Processor instance
 */
export function getAudioProcessor() {
  if (!audioProcessorInstance) {
    audioProcessorInstance = new AudioProcessor();
  }
  return audioProcessorInstance;
}

/**
 * Reset the singleton (for testing)
 */
export async function resetAudioProcessor() {
  if (audioProcessorInstance) {
    await audioProcessorInstance.destroy();
    audioProcessorInstance = null;
  }
}

export default AudioProcessor;
